/* eslint-disable react/jsx-curly-brace-presence */
/* eslint-disable react/destructuring-assignment */
import { CloseOutlined } from "@ant-design/icons";
import { useLazyQuery } from "@apollo/client";
import { Button, Divider, Dropdown, message, Popover, Skeleton } from "antd";
import { isEmpty } from "lodash";
import React, { useContext } from "react";
import { useSelector } from "react-redux";
import { GET_AI_RESPONSE } from "../../../graphql/query";
import { AiStarsIcon, LeafIcon } from "../../../icons/js/EmailAiIcons";

import { UserContext } from "../../../amplify/authenticator-provider";
import { applicationUseCaseMapping, getApplicationUseCaseId } from "../../../library/utils";

/**
 *
 * @param {{header:import("react").ReactNode,icon:import("react").ReactNode,close:()=>void,text:string,replaceText:(string)=>void}} props
 * @returns
 */
export const RewriteText = props => {
  const { user } = useContext(UserContext);
  const { selectedLead } = useSelector(state => state.lead);
  const { applicationUseCases } = useSelector(state => state.aiData);
  const [generatedText, setGeneratedText] = React.useState("");
  const [selectedTones, setSelectedTones] = React.useState(props?.tone ? [props?.tone] : []);
  const [getAiResponse, { loading: getAiResponseLoader }] = useLazyQuery(GET_AI_RESPONSE);

  const handleAiSuggestion = async () => {
    try {
      const res = await getAiResponse({
        fetchPolicy: "network-only",
        variables: {
          aiSuggestionUserInput: {
            quickTips: [],
            tones: selectedTones,
            additionalNote: `${props?.aiName} the following text: ${props.text}`,
          },
          companyId: user.company.id,
          applicationUseCaseId: getApplicationUseCaseId(
            applicationUseCases,
            applicationUseCaseMapping.ENGAGEMENT_EMAIL_SUGGESTION
          )?.id,
          leadId: selectedLead.id,
        },
      });

      if (res.data.getAiResponse.statusCode === 200) {
        const bodyContent = !isEmpty(res.data.getAiResponse.body)
          ? res.data.getAiResponse.body
          : !isEmpty(res.data.getAiResponse.bodyHtml)
          ? res.data.getAiResponse.bodyHtml
          : res.data.getAiResponse.content;
        setGeneratedText(bodyContent.replace(/>>>BODY:/g, ""));
      } else {
        message.error(res.data.getAiResponse.statusMessage);
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div className="flex-col-centered w-[450px] -mx-3 -mb-3">
      <div className="flex-row-centered justify-between px-3">
        <div className="flex-row-centered gap-2">
          {props.icon}
          <span className="sm-600 text-grey-700">{props.header}</span>
        </div>
        <CloseOutlined onClick={props.close} className="cursor-pointer" />
      </div>
      <div className="h-[1px] w-full bg-grey-200 my-2" />
      <div className="px-4 py-3">
        <p className="sm-600 text-grey-700 mb-1">Original Text</p>
        <p className="sm-400 text-grey-700">{props.text}</p>
      </div>

      {generatedText && <Divider className="!my-2 border-grey-300" />}

      {props?.name === "enhanceTone" && (
        <div className="px-4 py-3">
          <p className="sm-600 text-grey-700 mb-1">Select a tone</p>
          <p className="sm-400 text-grey-700 flex-row-centered gap-2 justfy-center align-center">
            {["Polite", "Funny", "Formal"].map((item, index) => (
              <span
                key={`key___${index.toString()}`}
                onClick={() => {
                  if (selectedTones.includes(item)) {
                    setSelectedTones(selectedTones.filter(i => i !== item));
                  } else {
                    setSelectedTones([...selectedTones, item]);
                  }
                }}
                style={{
                  backgroundColor: selectedTones.includes(item) ? "#E6F7FF" : "white",
                  border: selectedTones.includes(item) ? "1px solid #E2E8F0" : "1px solid #E6F7FF",
                }}
                className="cursor-pointer px-2 py-1 gap-2 flex-row-centered border border-solid rounded-[27px] border-grey-200"
              >
                {item}
              </span>
            ))}
          </p>
        </div>
      )}

      {generatedText ? (
        <div className="px-4 py-3">
          <p className="sm-600 text-grey-700 mb-1">
            {props?.name === "enhanceTone" ? "Enhanced Text" : "Rewritten Text"}
          </p>
          <p className="sm-400 text-grey-700" dangerouslySetInnerHTML={{ __html: generatedText }} />
        </div>
      ) : null}
      {getAiResponseLoader ? (
        <div className="px-4 py-3">
          <Skeleton active paragraph={{ rows: 2 }} />
        </div>
      ) : null}

      <div className="flex-row-centered justify-between px-4 py-3 bg-grey-50  rounded-b-md">
        <div className="flex-row-centered gap-1">
          <Button onClick={handleAiSuggestion} loading={getAiResponseLoader}>
            <AiStarsIcon />
            Rewrite
          </Button>
          {generatedText && (
            <Button
              onClick={() => {
                if (props?.replaceText) {
                  props.replaceText(generatedText);
                }
                props.close();
              }}
              type="primary"
            >
              Replace text
            </Button>
          )}
        </div>
        {/* <div className="flex-row-centered gap-3">
          <LeftOutlined className={`cursor-pointer`} />
          1/2
          <RightOutlined className={`cursor-pointer`} />
        </div> */}
      </div>
    </div>
  );
};

/**
 * @typedef {{open:boolean,type:string,component:import("react").ReactNode}} showGeneratePopoverData
 * @typedef {{text:string,top:number,left:number}} SelectionData
 */

/**
 *
 * @param {{popoverPosition:{x:number,y:number},setshowGeneratePopover:import("react").Dispatch<import("react").SetStateAction<showGeneratePopoverData>>,close:()=>void,showGeneratePopover:showGeneratePopoverData,aiDropdownOpen:boolean,setaiDropdownOpen:import("react").Dispatch<import("react").SetStateAction<boolean>>,selection:SelectionData,setselection:import("react").Dispatch<import("react").SetStateAction<SelectionData>>,replaceText:(string)=>void,children:import("react").ReactNode}} props
 * @returns
 */
export const AiPopoverForEmail = ({ children, ...props }) => {
  return (
    <Dropdown
      open={props.aiDropdownOpen}
      overlayStyle={{
        position: "fixed",
        left: `${props.popoverPosition.x}px`,
        top: `${props.popoverPosition.y}px`,
      }}
      arrow={false}
      menu={{
        items: [
          {
            label: "Rewrite text",
            onClick: () => {
              props.setshowGeneratePopover({
                open: true,
                type: "rewriteText",
                component: (
                  <RewriteText
                    close={props.close}
                    header="Rewrite Text"
                    icon={<LeafIcon />}
                    key="rewriteText"
                    name="rewriteText"
                    aiName="rewrite text"
                    text={props.selection.text}
                    replaceText={props.replaceText}
                  />
                ),
              });
              props.setaiDropdownOpen(false);
            },
          },
          {
            label: "Expand text",
            onClick: () => {
              props.setshowGeneratePopover({
                open: true,
                type: "expandText",
                component: (
                  <RewriteText
                    close={props.close}
                    header="Expand Text"
                    icon={<LeafIcon />}
                    key="expandText"
                    name="expandText"
                    aiName="expand text"
                    text={props.selection.text}
                    replaceText={props.replaceText}
                  />
                ),
              });
              props.setaiDropdownOpen(false);
            },
          },
          {
            label: "Shorten text",
            onClick: () => {
              props.setshowGeneratePopover({
                open: true,
                type: "shortenText",
                component: (
                  <RewriteText
                    close={props.close}
                    header="Shorten Text"
                    icon={<LeafIcon />}
                    key="shortenText"
                    name="shortenText"
                    aiName="shorten text"
                    text={props.selection.text}
                    replaceText={props.replaceText}
                  />
                ),
              });
              props.setaiDropdownOpen(false);
            },
          },
          {
            label: "Enhance tone",
            children: ["Polite", "Funny", "Formal"].map((item, index) => ({
              label: item,
              key: `tone___${index.toString()}`,
              onClick: () => {
                props.setshowGeneratePopover({
                  open: true,
                  type: "enhanceTone",
                  component: (
                    <RewriteText
                      close={props.close}
                      header="Enhance Tone"
                      icon={<LeafIcon />}
                      key="enhanceTone"
                      name="enhanceTone"
                      aiName="enhance text"
                      text={props.selection.text}
                      replaceText={props.replaceText}
                      tone={item}
                    />
                  ),
                });
                props.setaiDropdownOpen(false);
              },
            })),
          },
        ],
      }}
    >
      <Popover
        arrow={false}
        trigger="click"
        open={props.showGeneratePopover.open}
        content={props.showGeneratePopover.component}
      >
        {children}
      </Popover>
    </Dropdown>
  );
};
